/**
 * User display preferences.
 *
 * These let a user hide/show individual sections on the Project page and the
 * Lead page, and switch the Lead page into a "chart only" mode.
 *
 * NOTE (for backend): the shape below is what the UI sends/receives. The actual
 * persistence APIs do not exist yet — they are mocked in `UserPreferenceService`
 * (localStorage). See `docs/user-preferences-backend-requirements.md` for the
 * endpoints the backend team needs to build.
 */

/** A single hide/show toggle, used to render the preference list generically. */
export interface PreferenceToggleMeta {
  /** Maps to a boolean key on the relevant preference object. */
  key: string;
  /** Human label shown in the Preferences tab. */
  label: string;
  /** Optional helper text shown under the label. */
  hint?: string;
}

/** Project page section visibility. */
export interface ProjectPagePreferences {
  /** Project hero/overview block (image, address, developer, etc.). */
  showOverview: boolean;
  /** "Project Summary" cards row (Pricelist / Leads / Appointments / EOI). */
  showSummary: boolean;
  /** "More Information" panel (introduction, features, upgrades). */
  showMoreInfo: boolean;
}

/**
 * Filters that should be pre-selected when the Lead page first opens.
 * Values are stored as tag names / option values (the same things the Lead page
 * filter controls bind to).
 */
export interface LeadDefaultFilters {
  /** Default Lead Status selections (tag names). */
  leadStatus: string[];
  /** Default Lead Type selections (tag names). */
  leadType: string[];
  /** Default Status Type selections, e.g. ['Active']. */
  statusType: string[];
  /** Default Lead Interest / temperature (single tag name). */
  leadInterest: string | null;
  /** Default Follow Up option value, e.g. 'today'. */
  followUp: string | null;
}

/**
 * Per-field visibility for the Lead page filter toolbar. Each flag hides/shows a
 * single filter control (the whole toolbar is still gated by `showFilters`).
 */
export interface LeadFilterFieldsVisibility {
  search: boolean;
  projects: boolean;
  dateRange: boolean;
  listings: boolean;
  leadType: boolean;
  leadStatus: boolean;
  statusType: boolean;
  accessAgents: boolean;
  agents: boolean;
  bed: boolean;
  priceRange: boolean;
  timeFrame: boolean;
  leadInterest: boolean;
  followUp: boolean;
  groupby: boolean;
}

/** Lead page section visibility + chart behaviour. */
export interface LeadPagePreferences {
  /** Filter toolbar (search, date range, lead type/status, agents…). */
  showFilters: boolean;
  /** Status bar-chart + grouped status rows block. */
  showChart: boolean;
  /** Grouped status rows ("tabs") under the chart. */
  showStatusTabs: boolean;
  /** Main leads data table. */
  showTable: boolean;
  /**
   * When true: the status rows ("tabs") are hidden and ONLY the chart is shown.
   * Clicking a chart bar applies that status as a filter to the main table
   * instead of expanding an inline group.
   */
  chartOnlyMode: boolean;
  /** Which filter controls are visible in the filter toolbar. */
  filterFields: LeadFilterFieldsVisibility;
  /** Filters pre-applied when the Lead page opens. */
  defaultFilters: LeadDefaultFilters;
  /** Persisted follow-up sort order for the Lead follow-up screens (default: newest first). */
  followUpSort: SortPreference;
  /**
   * Inactive lead statuses (tag names) this user wants shown ALONGSIDE their active leads.
   * Empty (the default) keeps the long-standing behaviour: every inactive status is hidden
   * until the user filters Status Type to "Inactive" by hand. The list only ever widens the
   * result set — an explicit Inactive filter still returns every inactive status.
   */
  visibleInactiveStatuses: string[];
}

/**
 * A column sort preference (field + direction), reused by the follow-up grids so
 * a user's chosen order (e.g. newest follow-up first) persists between visits.
 * `sort_order` mirrors the existing `sort_order_field` shape used by the grids:
 * `false` = descending (newest first), `true` = ascending.
 */
export interface SortPreference {
  field: string;
  sort_order: boolean;
}

/** How leads opened from a dashboard graph result are presented. */
export type LeadResultDisplayMode = 'side-panel' | 'full-grid' | 'pop-out';

/**
 * Saved layout for a single dashboard widget. An empty `layout` array means
 * "use the built-in default layout"; the dashboard fills in any unknown widgets.
 */
export interface DashboardWidgetLayout {
  /** Stable widget key (matches the widget's registry id). */
  key: string;
  /** Column span in the 12-col builder grid. */
  cols?: number;
  /** Display order (lower = earlier). */
  order?: number;
  /** Whether the widget is shown. */
  visible?: boolean;
}

/** Dashboard page preferences (layout + graph/lead behaviour). */
export interface DashboardPagePreferences {
  /** Default Lead view on the dashboard; 'All' unless the user changes it. */
  leadView: string;
  /** How graph-result leads open (side panel / full-width grid / pop-out). */
  leadResultDisplayMode: LeadResultDisplayMode;
  /** Saved widget layout (order/size/visibility). Empty = default layout. */
  layout: DashboardWidgetLayout[];
  /** Keep filter controls visible when a graph widget is expanded full-size. */
  keepFiltersInFullGraph: boolean;
}

/**
 * A single panel's free-form grid position/size on the new dashboard (`/dashboard-new`), in grid
 * units — see `IDbnWidgetLayoutItem` in `modules/dashboard-new/dbn-widget-layout.model.ts` for the
 * authoritative shape (kept here as a structural duplicate so this file doesn't import a feature
 * module). `x`/`y` are the top-left grid cell (0-based), `w`/`h` the span in columns/row-units.
 */
export interface DashboardNewWidgetLayout {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** New dashboard (`/dashboard-new`) preferences — its widget grid layout and which widgets show. */
export interface DashboardNewPagePreferences {
  /** Saved widget positions/sizes. Empty = use the page's built-in default arrangement. */
  layout: DashboardNewWidgetLayout[];
  /** Widget keys the user chose to show. Absent (never customised) = the default set for the
   *  user's role (`DBN_ROLE_DEFAULT_WIDGETS` in `dbn-widget-layout.model.ts`), so role defaults keep
   *  applying until the user picks their own. */
  visibleWidgets?: string[] | null;
  /** Widget keys in the order the user arranged them in the Customise panel — positions in
   *  `layout` are derived from this. Absent on documents saved before ordering existed. */
  order?: string[] | null;
  /** The project last picked in the hero's project selector — reopened on the next visit (while
   *  it's still one of the user's active projects). */
  selectedProjectId?: string | null;
}

/**
 * Per-page filter visibility for non-lead list pages (Projects, Project Leads).
 * Keyed by filter id so each page can carry its own set without a new interface
 * per page — the page supplies the matching toggle metadata.
 */
export type FilterFieldsVisibility = Record<string, boolean>;

/** Projects page section/filter preferences. */
export interface ProjectListPagePreferences {
  /** Filter toolbar visibility (whole toolbar). */
  showFilters: boolean;
  /** Which individual filter controls are visible on the Projects page. */
  filterFields: FilterFieldsVisibility;
}

/** Project Leads page preferences (follow-up order + filter visibility). */
export interface ProjectLeadsPagePreferences {
  /** Filter toolbar visibility (whole toolbar). */
  showFilters: boolean;
  /** Which individual filter controls are visible on the Project Leads page. */
  filterFields: FilterFieldsVisibility;
  /** Persisted follow-up sort order (default: newest first). */
  followUpSort: SortPreference;
}

/**
 * Generic per-page filter visibility for the My-Listings pages. Shared by the
 * grid + list views of a page (they render the same filter set), so hiding a
 * filter is consistent whichever view the user is on.
 *   - `properties_page`: Properties grid-view + list-view
 *   - `listings_page`:   Listings props-grid + props-list
 */
export interface ListingFilterPagePreferences {
  /** Filter toolbar visibility (whole toolbar). */
  showFilters: boolean;
  /** Which individual filter controls are visible. */
  filterFields: FilterFieldsVisibility;
}

/** Full preference payload for a user. */
/**
 * Remmi Chat: the people and groups a user has starred.
 *
 * Lives here rather than in the chat module because chat has no preferences endpoint of its own,
 * and a favourite is exactly this kind of per-user display setting. People are keyed by `user_id`
 * (a colleague can be starred before any conversation exists), groups by `conversation_id`.
 */
export interface ChatPreferences {
  favouritePeople: string[];
  favouriteGroups: string[];
}

/**
 * Project Set Up side-menu state for one project.
 *
 * Stored under `project_setup_nav`, keyed by project id, and merged per project id by the API —
 * saving one project can never wipe another's entry. See
 * `docs/backend/projects/project-setup-nav-state.md`.
 */
export interface ProjectSetupNavState {
  /** Menu column closed on this project. Only a literal `true` counts as collapsed. */
  collapsed: boolean;
  /** Group the user last expanded. Stored by the API; the menu derives this from the URL today. */
  openGroup?: string | null;
  /** Server-stamped — anything sent from here is discarded. */
  updated_at?: string;
}

export interface SalesExportViewPreference {
  name: string;
  settings: Record<string, any>;
}

export interface UserPreferences {
  user_id: string;
  project_page: ProjectPagePreferences;
  lead_page: LeadPagePreferences;
  /** Dashboard layout + graph/lead-result behaviour. */
  dashboard_page: DashboardPagePreferences;
  /** New dashboard (`/dashboard-new`) widget grid layout. */
  dashboard_new_page: DashboardNewPagePreferences;
  /** Projects list page filter visibility (per-page, separate from leads). */
  project_list_page: ProjectListPagePreferences;
  /** Project Leads page follow-up order + filter visibility. */
  project_leads_page: ProjectLeadsPagePreferences;
  /** My-Listings › Properties page (grid + list views) filter visibility. */
  properties_page: ListingFilterPagePreferences;
  /** My-Listings › Listings page (grid + list views) filter visibility. */
  listings_page: ListingFilterPagePreferences;
  /** Contract Agreements list page filter visibility. */
  contract_list_page: ListingFilterPagePreferences;
  /** Remmi Chat favourites (starred people and groups). */
  chat: ChatPreferences;
  /** Project Set Up side-menu state, keyed by project id. */
  project_setup_nav: Record<string, ProjectSetupNavState>;
  /**
   * Project Sales Report "Save as View" export settings. Not in the backend's known keys — the API
   * keeps unknown top-level keys as-is (`buildPreferences` in remmi-node's userDisplayPreference.model).
   */
  sales_export_views?: SalesExportViewPreference[];
  /** ISO timestamp of last save (set by the mock service). */
  updated_at?: string;
}

/** Default dashboard "Lead view" — "All" unless the user changes it (req 3.1). */
export const DEFAULT_LEAD_VIEW = 'All';

/**
 * Default follow-up sort: newest first. `sort_order: false` = descending, matching
 * the grids' existing `sort_order_field` convention.
 */
export const DEFAULT_FOLLOW_UP_SORT: SortPreference = { field: 'follow-up_appt', sort_order: false };

/** Factory for the default (everything visible) preferences. */
export function defaultUserPreferences(user_id: string = ''): UserPreferences {
  return {
    user_id,
    project_page: {
      showOverview: true,
      showSummary: true,
      showMoreInfo: true,
    },
    lead_page: {
      showFilters: true,
      showChart: true,
      showStatusTabs: true,
      showTable: true,
      chartOnlyMode: false,
      filterFields: {
        search: true,
        projects: true,
        dateRange: true,
        listings: true,
        leadType: true,
        leadStatus: true,
        statusType: true,
        accessAgents: true,
        agents: true,
        bed: true,
        priceRange: true,
        timeFrame: true,
        leadInterest: true,
        followUp: true,
        groupby: true,
      },
      defaultFilters: {
        leadStatus: [],
        leadType: [],
        statusType: ['Active'],
        leadInterest: null,
        followUp: null,
      },
      followUpSort: { ...DEFAULT_FOLLOW_UP_SORT },
      visibleInactiveStatuses: [],
    },
    dashboard_page: {
      leadView: DEFAULT_LEAD_VIEW,
      leadResultDisplayMode: 'side-panel',
      layout: [],
      keepFiltersInFullGraph: true,
    },
    dashboard_new_page: {
      layout: [],
    },
    project_list_page: {
      showFilters: true,
      filterFields: {},
    },
    project_leads_page: {
      showFilters: true,
      // Project-Leads has its OWN filter-visibility set (separate from the Leads
      // page). Keys mirror PROJECT_LEAD_FILTER_FIELD_TOGGLES; default everything on.
      filterFields: {
        search: true,
        dateRange: true,
        leadType: true,
        leadStatus: true,
        statusType: true,
        agents: true,
        leadInterest: true,
        followUp: true,
        groupby: true,
      },
      followUpSort: { ...DEFAULT_FOLLOW_UP_SORT },
    },
    properties_page: {
      showFilters: true,
      // Keys mirror PROPERTIES_FILTER_FIELD_TOGGLES; default everything on.
      filterFields: {
        search: true,
        suburb: true,
        propertyType: true,
        propertyStatus: true,
        bedrooms: true,
        landSize: true,
        buildingSize: true,
        features: true,
      },
    },
    listings_page: {
      showFilters: true,
      // Keys mirror LISTINGS_FILTER_FIELD_TOGGLES; default everything on.
      filterFields: {
        search: true,
        propertyType: true,
        suburb: true,
        listingStatus: true,
        listingType: true,
        agent: true,
        contractStatus: true,
        approvals: true,
        listingDate: true,
      },
    },
    contract_list_page: {
      showFilters: true,
      // Keys mirror CONTRACT_LIST_FILTER_FIELD_TOGGLES; default everything on.
      filterFields: {
        search: true,
        agent: true,
        listing: true,
        project: true,
        contractStatus: true,
        conditionDue: true,
        settlementDue: true,
      },
    },
    chat: {
      favouritePeople: [],
      favouriteGroups: [],
    },
    // No entry for a project means its side menu is open.
    project_setup_nav: {},
  };
}

/** Static Follow Up options (mirrors the Lead page filter). */
export const FOLLOW_UP_OPTIONS: { label: string; value: string }[] = [
  { label: 'All', value: 'all' },
  { label: 'Morning', value: 'morning' },
  { label: 'Today', value: 'today' },
  { label: 'Tomorrow', value: 'tomorrow' },
  { label: 'This Week', value: 'this week' },
  { label: 'Next Week', value: 'next week' },
  { label: 'This Afternoon', value: 'afternoon' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Overdue', value: 'overdue' },
];

/** Metadata driving the Project page toggle list in the Preferences tab. */
export const PROJECT_PAGE_TOGGLES: PreferenceToggleMeta[] = [
  { key: 'showOverview', label: 'Project Overview', hint: 'Hero image, address, developer and price details.' },
  { key: 'showSummary', label: 'Project Summary cards', hint: 'Pricelist, Leads, Appointments and EOI count cards.' },
  { key: 'showMoreInfo', label: 'More Information', hint: 'Introduction, features and project upgrades.' },
];

/**
 * Metadata driving the Leads (management) page "filter fields" toggle list.
 * Order mirrors the Leads page toolbar. (Access Agents dropped — no longer a filter.)
 */
export const LEAD_FILTER_FIELD_TOGGLES: PreferenceToggleMeta[] = [
  { key: 'search', label: 'Search' },
  { key: 'projects', label: 'Projects' },
  { key: 'dateRange', label: 'Date Range' },
  { key: 'listings', label: 'Listings' },
  { key: 'leadType', label: 'Lead Type' },
  { key: 'leadStatus', label: 'Lead Status' },
  { key: 'agents', label: 'Agents' },
  { key: 'bed', label: 'Bed' },
  { key: 'priceRange', label: 'Price Range' },
  { key: 'timeFrame', label: 'Time Frame' },
  { key: 'followUp', label: 'Follow Up' },
  { key: 'leadInterest', label: 'Lead Interest' },
  { key: 'statusType', label: 'Status Type' },
  { key: 'groupby', label: 'Group By' },
];

/**
 * Metadata driving the Project-Leads "filter fields" toggle list.
 * Order mirrors the Project-Leads toolbar, which differs from the Leads page.
 * (No Access Agents — that filter never existed on Project-Leads.)
 */
export const PROJECT_LEAD_FILTER_FIELD_TOGGLES: PreferenceToggleMeta[] = [
  { key: 'search', label: 'Search' },
  { key: 'dateRange', label: 'Date Range' },
  { key: 'leadType', label: 'Lead Type' },
  { key: 'leadStatus', label: 'Lead Status' },
  { key: 'statusType', label: 'Status Type' },
  { key: 'agents', label: 'Agents' },
  { key: 'leadInterest', label: 'Lead Interest' },
  { key: 'followUp', label: 'Follow Up' },
  { key: 'groupby', label: 'Group By' },
];

/**
 * Metadata driving the My-Listings › Properties page (grid + list views)
 * "filter fields" toggle list. Order mirrors the Properties toolbar.
 */
export const PROPERTIES_FILTER_FIELD_TOGGLES: PreferenceToggleMeta[] = [
  { key: 'search', label: 'Search' },
  { key: 'suburb', label: 'Suburb' },
  { key: 'propertyType', label: 'Property Type' },
  { key: 'propertyStatus', label: 'Property Status' },
  { key: 'bedrooms', label: 'Bedrooms' },
  { key: 'landSize', label: 'Land Size' },
  { key: 'buildingSize', label: 'Building Size' },
  { key: 'features', label: 'Features' },
];

/**
 * Metadata driving the My-Listings › Listings page (grid + list views)
 * "filter fields" toggle list. Order mirrors the Listings toolbar.
 */
export const LISTINGS_FILTER_FIELD_TOGGLES: PreferenceToggleMeta[] = [
  { key: 'search', label: 'Search' },
  { key: 'propertyType', label: 'Property Type' },
  { key: 'suburb', label: 'Suburb' },
  { key: 'listingStatus', label: 'Listing Status' },
  { key: 'listingType', label: 'Listing Type' },
  { key: 'agent', label: 'Agent' },
  { key: 'contractStatus', label: 'Contract Status' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'listingDate', label: 'Listing Creation Date' },
];

/**
 * Metadata driving the Contract Agreements list page "filter fields" toggle list.
 * Order mirrors the contract-list toolbar.
 */
export const CONTRACT_LIST_FILTER_FIELD_TOGGLES: PreferenceToggleMeta[] = [
  { key: 'search', label: 'Search' },
  { key: 'agent', label: 'Agent' },
  { key: 'listing', label: 'Listing' },
  { key: 'project', label: 'Project' },
  { key: 'contractStatus', label: 'Contract Status' },
  { key: 'conditionDue', label: 'Condition Due' },
  { key: 'settlementDue', label: 'Settlement Due' },
];

/** Metadata driving the Lead page toggle list in the Preferences tab. */
export const LEAD_PAGE_TOGGLES: PreferenceToggleMeta[] = [
  { key: 'showFilters', label: 'Filters toolbar', hint: 'Search, date range, lead type/status and agent filters.' },
  { key: 'showChart', label: 'Status chart', hint: 'The lead-status bar chart and grouped rows.' },
  { key: 'showStatusTabs', label: 'Status tabs (groups)', hint: 'Expandable status rows shown beside the chart.' },
  { key: 'showTable', label: 'Leads table', hint: 'The main leads data table.' },
];
